import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine,
} from 'recharts';
import { SurveillanceDevice } from './surveillanceShared';
import { fmtDateTimeFull } from '../../utils/format';
import { ui } from '../../styles/shared';
import {
    fetchDeviceParamsForReport, fetchMonthlyReport,
    DeviceParam, MonthlyReportData, AlertEvent,
} from '../../services/reportsApi';

// ── Constants ────────────────────────────────────────────────────────────────

const MONTH_NAMES = [
    'Januari', 'Februari', 'Mars', 'April', 'Maj', 'Juni',
    'Juli', 'Augusti', 'September', 'Oktober', 'November', 'December',
];

const DAY_ABBR = ['Sö', 'Må', 'Ti', 'On', 'To', 'Fr', 'Lö'];

// ── Helpers ──────────────────────────────────────────────────────────────────

interface Week { label: string; from: Date; to: Date; weekNo: number }

function getWeeksForMonth(year: number, month: number): Week[] {
    // month is 1-based
    const monthStart = new Date(year, month - 1, 1, 0, 0, 0, 0);
    const monthEnd   = new Date(year, month, 0, 23, 59, 59, 999); // last day

    // Find the Monday on or before monthStart
    const firstDay = new Date(monthStart);
    const dow = firstDay.getDay(); // 0=Sun
    firstDay.setDate(firstDay.getDate() - (dow === 0 ? 6 : dow - 1));

    const weeks: Week[] = [];
    let weekStart = new Date(firstDay);

    while (weekStart <= monthEnd) {
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);
        weekEnd.setHours(23, 59, 59, 999);

        // Clip to month boundaries
        const from = new Date(Math.max(weekStart.getTime(), monthStart.getTime()));
        const to   = new Date(Math.min(weekEnd.getTime(),   monthEnd.getTime()));

        // ISO week number (Thursday of the week determines the year)
        const thu = new Date(weekStart);
        thu.setDate(thu.getDate() + 3);
        const yearStart = new Date(thu.getFullYear(), 0, 1);
        const weekNo = Math.ceil(((thu.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);

        const fmtD = (d: Date) => `${d.getDate()}/${d.getMonth() + 1}`;
        weeks.push({ label: `Vecka ${weekNo} (${fmtD(from)}–${fmtD(to)})`, from, to, weekNo });

        weekStart = new Date(weekStart);
        weekStart.setDate(weekStart.getDate() + 7);
    }
    return weeks;
}

function fmtTs(ts: number): string {
    const d = new Date(ts);
    return `${DAY_ABBR[d.getDay()]} ${String(d.getHours()).padStart(2, '0')}:00`;
}

const fmtDateTime = (dt: string | null): string => fmtDateTimeFull(dt);

/** Fasta tick var 12:e timme (00:00 och 12:00 lokal tid) over veckan,
 *  sa att axeln ser likadan ut oavsett hur manga matvarden/larm som finns. */
function weekTicks(from: Date, to: Date): number[] {
    const ticks: number[] = [];
    const d = new Date(from.getFullYear(), from.getMonth(), from.getDate(), 0, 0, 0, 0);
    while (d.getTime() <= to.getTime()) {
        if (d.getTime() >= from.getTime()) ticks.push(d.getTime());
        d.setHours(d.getHours() + 12);
    }
    return ticks;
}

interface DeviationPair {
    trigger:  AlertEvent | null;
    recovery: AlertEvent | null;
}

function buildDeviationPairs(events: AlertEvent[]): DeviationPair[] {
    const triggers   = events.filter(e => !e.is_recovery);
    const recoveries = events.filter(e => e.is_recovery);
    const pairs: DeviationPair[] = [];
    const usedIds = new Set<number>();

    for (const t of triggers) {
        const r = recoveries.find(
            rc => rc.rule_id === t.rule_id &&
                  new Date(rc.triggered_at) > new Date(t.triggered_at) &&
                  !usedIds.has(rc.id),
        );
        if (r) usedIds.add(r.id);
        pairs.push({ trigger: t, recovery: r ?? null });
    }
    // Recoveries whose trigger falls outside the month
    for (const r of recoveries) {
        if (!usedIds.has(r.id)) pairs.push({ trigger: null, recovery: r });
    }
    return pairs.sort((a, b) => {
        const aTime = new Date((a.trigger ?? a.recovery)!.triggered_at).getTime();
        const bTime = new Date((b.trigger ?? b.recovery)!.triggered_at).getTime();
        return aTime - bTime;
    });
}

// ── Sub-components ───────────────────────────────────────────────────────────

const PRINT_STYLE = `
@media print {
  body * { visibility: hidden !important; }
  #monthly-report-content, #monthly-report-content * { visibility: visible !important; }
  #monthly-report-content {
    position: absolute !important; top: 0 !important; left: 0 !important;
    width: 100% !important; padding: 16px !important; background: white !important;
    box-sizing: border-box !important;
  }
  .report-no-print { display: none !important; }
  .chart-scroll-wrapper { overflow: visible !important; }
}
`;

function handlePrint() {
    const existing = document.getElementById('report-print-style');
    if (!existing) {
        const s = document.createElement('style');
        s.id = 'report-print-style';
        s.textContent = PRINT_STYLE;
        document.head.appendChild(s);
    }
    const cleanup = () => {
        document.getElementById('report-print-style')?.remove();
        window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup);
    window.print();
}

interface WeekChartProps {
    week: Week;
    readings: MonthlyReportData['readings'];
    alertEvents: AlertEvent[];
    unit: string | null;
}

const WeekChart: React.FC<WeekChartProps> = ({ week, readings, alertEvents, unit }) => {
    const data = readings.filter(r => r.ts >= week.from.getTime() && r.ts <= week.to.getTime());
    const rawEvents = alertEvents.filter(e => {
        const t = new Date(e.triggered_at).getTime();
        return t >= week.from.getTime() && t <= week.to.getTime();
    });

    // Deduplicate: one marker per rule per event type (trigger / recovery).
    // Multiple alert_history rows for the same episode stack on top of each
    // other and make the chart unreadable.
    const shownKeys = new Set<string>();
    const events = rawEvents.filter(e => {
        const key = `${e.rule_id}_${e.is_recovery ? 1 : 0}`;
        if (shownKeys.has(key)) return false;
        shownKeys.add(key);
        return true;
    });

    if (data.length === 0) {
        return <p style={{ color: '#999', fontSize: 13, margin: '4px 0 0 8px' }}>Inga mätvärden denna vecka.</p>;
    }

    const yLabel = unit ? `${unit}` : undefined;

    return (
        <>
            <div className="chart-rotate-hint" style={{ marginBottom: 4 }}>
                <span>↻</span>
                <span>Vrid telefonen eller dra i sidled för att se hela diagrammet</span>
            </div>
            <div className="chart-scroll-wrapper" style={{ overflowX: 'auto' }}>
            <LineChart width={900} height={220} data={data}
                margin={{ top: 10, right: 30, left: yLabel ? 16 : 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                    dataKey="ts"
                    type="number"
                    scale="time"
                    domain={[week.from.getTime(), week.to.getTime()]}
                    ticks={weekTicks(week.from, week.to)}
                    interval={0}
                    tickFormatter={fmtTs}
                    tick={{ fontSize: 11 }}
                />
                <YAxis
                    tick={{ fontSize: 11 }}
                    width={yLabel ? 56 : 44}
                    label={yLabel ? { value: yLabel, angle: -90, position: 'insideLeft', offset: 8, style: { fontSize: 11 } } : undefined}
                />
                <Tooltip
                    labelFormatter={(ts: any) => new Date(Number(ts)).toLocaleString('sv-SE')}
                    formatter={(v: any) => [v, unit ?? 'Värde']}
                />
                <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#3b82f6"
                    dot={false}
                    strokeWidth={1.5}
                    name={unit ?? 'Värde'}
                />
                {events.map(e => (
                    <ReferenceLine
                        key={e.id}
                        x={new Date(e.triggered_at).getTime()}
                        stroke={e.is_recovery ? '#10b981' : '#ef4444'}
                        strokeWidth={1.5}
                        strokeDasharray="5 3"
                        label={{
                            value: e.is_recovery ? '✓' : '!',
                            position: 'top',
                            fill: e.is_recovery ? '#10b981' : '#ef4444',
                            fontSize: 11,
                            fontWeight: 700,
                        }}
                    />
                ))}
            </LineChart>
            </div>
        </>
    );
};

// ── Main component ────────────────────────────────────────────────────────────

interface ReportTabProps {
    devices: SurveillanceDevice[];
}

const currentYear  = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;
const YEAR_OPTIONS = Array.from({ length: 4 }, (_, i) => currentYear - 2 + i);

const ReportTab: React.FC<ReportTabProps> = ({ devices }) => {
    const [selectedIeee, setSelectedIeee]   = useState('');
    const [params, setParams]               = useState<DeviceParam[]>([]);
    const [paramsLoading, setParamsLoading] = useState(false);
    const [selectedParam, setSelectedParam] = useState('');
    const [year, setYear]   = useState(currentYear);
    const [month, setMonth] = useState(currentMonth);
    const [report, setReport]     = useState<MonthlyReportData | null>(null);
    const [loading, setLoading]   = useState(false);
    const [error, setError]       = useState<string | null>(null);

    // Load parameters when device changes
    useEffect(() => {
        if (!selectedIeee) { setParams([]); setSelectedParam(''); return; }
        setParamsLoading(true);
        fetchDeviceParamsForReport(selectedIeee).then(p => {
            setParams(p);
            setSelectedParam(p[0]?.key ?? '');
            setParamsLoading(false);
        });
    }, [selectedIeee]);

    const handleGenerate = useCallback(async () => {
        if (!selectedIeee || !selectedParam) return;
        setLoading(true);
        setError(null);
        setReport(null);
        const data = await fetchMonthlyReport(selectedIeee, selectedParam, year, month);
        if (!data || (data as any).error) {
            setError('Kunde inte hämta rapportdata.');
        } else {
            setReport(data);
        }
        setLoading(false);
    }, [selectedIeee, selectedParam, year, month]);

    const weeks = useMemo(() => report ? getWeeksForMonth(report.year, report.month) : [], [report]);

    const deviationPairs = useMemo(() =>
        report ? buildDeviationPairs(report.alertEvents) : [],
    [report]);

    const triggerCount   = report?.alertEvents.filter(e => !e.is_recovery).length ?? 0;
    const recoveryCount  = report?.alertEvents.filter(e => e.is_recovery).length ?? 0;
    const submittedCount = deviationPairs.filter(p => p.recovery?.submitted_at).length;
    const deviationCount = deviationPairs.filter(p => p.recovery?.deviation_token).length;

    const btnStyle: React.CSSProperties = {
        padding: '8px 18px', border: 'none', borderRadius: 6, cursor: 'pointer',
        fontWeight: 600, fontSize: 13,
    };
    const selectStyle: React.CSSProperties = {
        padding: '7px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13,
    };

    return (
        <div style={{ padding: '16px 0' }}>

            {/* ── Picker bar ───────────────────────────────────────────── */}
            <div className="report-no-print"
                style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 20 }}>

                <select style={selectStyle} value={selectedIeee}
                    onChange={e => { setSelectedIeee(e.target.value); setReport(null); }}>
                    <option value="">Välj sensor...</option>
                    {devices.map(d => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                </select>

                <select style={selectStyle} value={selectedParam} disabled={!params.length || paramsLoading}
                    onChange={e => { setSelectedParam(e.target.value); setReport(null); }}>
                    {paramsLoading
                        ? <option>Laddar...</option>
                        : params.length === 0
                            ? <option value="">— välj sensor —</option>
                            : params.map(p => (
                                <option key={p.key} value={p.key}>
                                    {p.name ?? p.key}{p.unit ? ` (${p.unit})` : ''}
                                </option>
                            ))
                    }
                </select>

                <select style={selectStyle} value={year} onChange={e => { setYear(Number(e.target.value)); setReport(null); }}>
                    {YEAR_OPTIONS.map(y => <option key={y} value={y}>{y}</option>)}
                </select>

                <select style={selectStyle} value={month} onChange={e => { setMonth(Number(e.target.value)); setReport(null); }}>
                    {MONTH_NAMES.map((name, i) => <option key={i + 1} value={i + 1}>{name}</option>)}
                </select>

                <button
                    style={{ ...btnStyle, background: '#0f3460', color: 'white' }}
                    disabled={!selectedIeee || !selectedParam || loading}
                    onClick={handleGenerate}>
                    {loading ? 'Hämtar…' : 'Generera rapport'}
                </button>

                {report && (
                    <button style={{ ...btnStyle, background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db' }}
                        onClick={handlePrint}>
                        🖨️ Skriv ut / Spara PDF
                    </button>
                )}
            </div>

            {error && (
                <div style={{ color: '#b91c1c', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 6, padding: '10px 14px', marginBottom: 16 }}>
                    {error}
                </div>
            )}

            {loading && (
                <div style={{ color: '#555', fontSize: 14, padding: 24 }}>Hämtar mätdata…</div>
            )}

            {/* ── Report content ────────────────────────────────────────── */}
            {report && (
                <div id="monthly-report-content">

                    {/* Title */}
                    <div style={{ marginBottom: 20, paddingBottom: 12, borderBottom: '2px solid #0f3460' }}>
                        <h2 style={{ margin: '0 0 4px', fontSize: 20, color: '#0f3460' }}>
                            Månadsrapport — {report.device.name}
                        </h2>
                        <div style={{ fontSize: 14, color: '#555' }}>
                            {report.parameter.name ?? report.parameter.key}
                            {report.parameter.unit ? ` (${report.parameter.unit})` : ''}
                            {' · '}{MONTH_NAMES[report.month - 1]} {report.year}
                        </div>
                        <div style={{ fontSize: 13, color: '#777', marginTop: 6, display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                            <span>🔴 Larm aktiverade: <strong>{triggerCount}</strong></span>
                            <span>🟢 Återhämtningar: <strong>{recoveryCount}</strong></span>
                            {deviationCount > 0 && (
                                <span>📋 Avvikelserapporter: <strong>{submittedCount}/{deviationCount}</strong> inskickade</span>
                            )}
                        </div>
                    </div>

                    {/* Datakälla: äldre mätvärden kommer ur långtidslagringen */}
                    {report.readingsSource !== 'detailed' && (
                        <div style={{ fontSize: 12, color: '#78350f', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6, padding: '8px 12px', marginBottom: 12 }}>
                            {report.readingsSource === 'downsampled'
                                ? 'Mätvärdena visas var 15:e minut från långtidslagringen, eftersom de detaljerade värdena har rensats.'
                                : `Fram till ${fmtDateTime(new Date(report.downsampledUntil!).toISOString())} visas mätvärden var 15:e minut från långtidslagringen. Därefter visas alla värden.`}
                        </div>
                    )}

                    {/* Legend */}
                    {report.alertEvents.length > 0 && (
                        <div className="report-no-print"
                            style={{ display: 'flex', gap: 16, fontSize: 12, color: '#555', marginBottom: 12 }}>
                            <span style={{ color: '#ef4444' }}>— — Larm utlöst</span>
                            <span style={{ color: '#10b981' }}>— — Återhämtad</span>
                        </div>
                    )}

                    {/* Weekly charts — show all weeks */}
                    {weeks.map(week => (
                        <div key={week.label}
                            style={{ marginBottom: 28, pageBreakInside: 'avoid' }}>
                            <h3 style={{ fontSize: 14, fontWeight: 600, color: '#374151', margin: '0 0 8px' }}>
                                {week.label}
                            </h3>
                            <WeekChart
                                week={week}
                                readings={report.readings}
                                alertEvents={report.alertEvents}
                                unit={report.parameter.unit}
                            />
                        </div>
                    ))
                    }
                    {report.alertEvents.length === 0 && report.readings.length > 0 && (
                        <p style={{ color: '#6b7280', fontSize: 13, marginTop: 8 }}>
                            Inga larmhändelser registrerades denna månad.
                        </p>
                    )}

                    {/* Deviation table */}
                    {deviationPairs.length > 0 && (
                        <div style={{ marginTop: 24, pageBreakBefore: 'auto' }}>
                            <h3 style={{ fontSize: 15, fontWeight: 600, color: '#0f3460', marginBottom: 10 }}>
                                Avvikelsehändelser
                            </h3>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                <thead>
                                    <tr style={{ background: '#f1f5f9' }}>
                                        <th style={th}>Regel</th>
                                        <th style={th}>Larm utlöst</th>
                                        <th style={th}>Återhämtad</th>
                                        <th style={th}>Rapporterad av</th>
                                        <th style={th}>Orsak</th>
                                        <th style={th}>Åtgärd</th>
                                        <th style={th}>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {deviationPairs.map((pair, i) => {
                                        const submitted = !!pair.recovery?.submitted_at;
                                        return (
                                            <tr key={i} style={{ borderBottom: '1px solid #e5e7eb', background: i % 2 ? '#f9fafb' : 'white' }}>
                                                <td style={td}>{pair.trigger?.rule_name ?? pair.recovery?.rule_name ?? '—'}</td>
                                                <td style={{ ...td, whiteSpace: 'nowrap', color: '#ef4444' }}>
                                                    {pair.trigger ? fmtDateTime(pair.trigger.triggered_at) : '(innan månaden)'}
                                                </td>
                                                <td style={{ ...td, whiteSpace: 'nowrap', color: '#10b981' }}>
                                                    {fmtDateTime(pair.recovery?.triggered_at ?? null)}
                                                </td>
                                                <td style={td}>{pair.recovery?.reporter_name || '—'}</td>
                                                <td style={td}>{pair.recovery?.reason || '—'}</td>
                                                <td style={td}>{pair.recovery?.action_taken || '—'}</td>
                                                <td style={{ ...td, textAlign: 'center' }}>
                                                    {!pair.recovery?.deviation_token
                                                        ? <span style={{ color: '#9ca3af', fontSize: 11 }}>Ej krävs</span>
                                                        : submitted
                                                            ? <span style={{ color: '#10b981', fontWeight: 600 }}>✅ Inskickad</span>
                                                            : <span style={{ color: '#f59e0b', fontWeight: 600 }}>⏳ Ej inskickad</span>
                                                    }
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {report.alertEvents.length === 0 && report.readings.length === 0 && (
                        <p style={{ color: '#9ca3af', fontSize: 14, padding: '20px 0' }}>
                            Inga mätvärden eller larmhändelser hittades för denna månad.
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

// ── Table cell styles ────────────────────────────────────────────────────────

// Stilar från den gemensamma stilfilen (tätare rader i rapporten)
const th: React.CSSProperties = { ...ui.th, padding: '8px 10px', fontSize: 12 };
const td: React.CSSProperties = { ...ui.td, padding: '8px 10px', borderBottom: undefined };

export default ReportTab;
